import React, { useState, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '../../../contexts/ThemeContext';
import { SQUAD_PRESETS, SquadPreset } from './squadPresets';
import { launchSquad } from '../../../services/squadService';
import { getStanceToken } from './chatDagTokens';

interface Props {
  sessionId: string;
  anchorMessageId: string;
  anchorLabel: string;
  triggerRect: DOMRect;
  onClose: () => void;
  onDispatched?: (squadId: string) => void;
}

export default function SquadLaunchPopover({
  sessionId, anchorMessageId, anchorLabel, triggerRect, onClose, onDispatched,
}: Props) {
  const { darkMode } = useTheme();
  const [selectedPreset, setSelectedPreset] = useState<SquadPreset | null>(null);
  const [fanWidth, setFanWidth] = useState(3);
  const [taskBrief, setTaskBrief] = useState('');
  const [dispatching, setDispatching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [onClose]);

  const handlePresetSelect = useCallback((preset: SquadPreset) => {
    setSelectedPreset(preset);
    setFanWidth(preset.fanWidth);
    setError(null);
  }, []);

  const handleDispatch = useCallback(async () => {
    if (!selectedPreset || !taskBrief.trim()) return;
    setDispatching(true);
    setError(null);
    try {
      const result = await launchSquad({
        sessionId,
        preset_key: selectedPreset.key,
        fan_width: fanWidth,
        task_brief: taskBrief.trim(),
        anchor_message_id: anchorMessageId,
      });
      if (result.ok) {
        onDispatched?.(result.squad_id);
        onClose();
      } else {
        setError('Launch failed');
      }
    } catch (e: any) {
      setError(e?.response?.data?.detail?.message || e.message || 'Launch failed');
    } finally {
      setDispatching(false);
    }
  }, [selectedPreset, fanWidth, taskBrief, sessionId, anchorMessageId, onClose, onDispatched]);

  const popoverContent = (
    <div
      ref={popoverRef}
      className={`fixed z-[600] rounded-lg border shadow-xl p-3 w-[320px] ${
        darkMode
          ? 'bg-gray-800 border-gray-600 text-gray-200'
          : 'bg-white border-gray-300 text-gray-800'
      }`}
      style={{
        top: triggerRect.bottom + 8,
        left: Math.max(8, triggerRect.left - 160),
      }}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold">Launch Squad</span>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-200 text-sm">✕</button>
      </div>
      <div className={`text-[10px] mb-3 truncate ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
        Anchor: {anchorLabel}
      </div>

      {!selectedPreset ? (
        <div className="space-y-1.5">
          {SQUAD_PRESETS.map((p) => (
            <button
              key={p.key}
              onClick={() => handlePresetSelect(p)}
              className={`w-full text-left rounded-md border px-2.5 py-2 text-[11px] transition-colors ${
                darkMode
                  ? 'border-gray-600 hover:border-indigo-500 hover:bg-indigo-950/30'
                  : 'border-gray-200 hover:border-indigo-400 hover:bg-indigo-50'
              }`}
            >
              <div className="font-medium">{p.label}</div>
              <div className={`mt-0.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {p.description}
              </div>
              <div className="mt-1 flex gap-1">
                {p.stances.map((s, i) => {
                  const t = getStanceToken(s);
                  return (
                    <span key={i} className={`text-[9px] px-1 rounded ${
                      darkMode ? 'bg-gray-700' : 'bg-gray-100'
                    }`}>{t.glyph} {t.label}</span>
                  );
                })}
              </div>
            </button>
          ))}
        </div>
      ) : (
        <div className="space-y-3">
          <div>
            <button
              onClick={() => setSelectedPreset(null)}
              className={`text-[10px] ${darkMode ? 'text-indigo-400' : 'text-indigo-600'} hover:underline`}
            >
              ← change preset
            </button>
            <div className="font-medium text-sm mt-1">{selectedPreset.label}</div>
          </div>
          <div>
            <label className={`text-[10px] block mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Fan width
            </label>
            <input
              type="number" min={1} max={8} value={fanWidth}
              onChange={(e) => setFanWidth(Math.max(1, Math.min(8, parseInt(e.target.value) || 1)))}
              className={`w-16 rounded border px-2 py-1 text-xs ${
                darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-300'
              }`}
            />
          </div>
          <div>
            <label className={`text-[10px] block mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Task brief
            </label>
            <textarea
              value={taskBrief}
              onChange={(e) => setTaskBrief(e.target.value)}
              placeholder="What should the squad discuss?"
              rows={2}
              className={`w-full rounded border px-2 py-1 text-xs resize-none ${
                darkMode ? 'bg-gray-700 border-gray-600 placeholder:text-gray-500'
                         : 'bg-gray-50 border-gray-300 placeholder:text-gray-400'
              }`}
            />
          </div>
          {error && <div className="text-[10px] text-rose-500">{error}</div>}
          <button
            onClick={handleDispatch}
            disabled={dispatching || !taskBrief.trim()}
            className={`w-full rounded-md py-1.5 text-xs font-medium transition-colors ${
              dispatching || !taskBrief.trim()
                ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                : darkMode
                  ? 'bg-indigo-600 hover:bg-indigo-500 text-white'
                  : 'bg-indigo-500 hover:bg-indigo-600 text-white'
            }`}
          >
            {dispatching ? 'Dispatching…' : `Launch ${fanWidth}-member squad`}
          </button>
        </div>
      )}
    </div>
  );

  return createPortal(popoverContent, document.body);
}
